<template>
  <div>
    <div class="attention-check el-input-group el-input-group--append el-input-group--prepend">
      <div>
        <div class="el-input-group el-input-group--append el-input-group--prepend">
          <div class="el-input-group__prepend">Is character</div>
          <character-select
            :selected="state.sourceCharacterKey"
            :characters="getSourceCharacters(emitter.caseConf)"
            :disabled="readonly"
            placeholder="Select character"
            @input="selectCharacter"
          />
          <div v-if="sourceCharacterErrors.length" class="el-input-group__append">
            <help-tooltip type="error">
              <div v-for="error in sourceCharacterErrors" :key="error.message">{{ error.message }}</div>
            </help-tooltip>
          </div>
        </div>
      </div>
      <div>
        <div class="el-input-group el-input-group--append el-input-group--prepend">
          <div class="el-input-group__prepend">Paying attention to</div>
          <character-select
            :selected="state.targetCharacterKey"
            :characters="targetCharacters"
            :disabled="readonly"
            placeholder="Select target"
            @input="selectTarget"
          />
          <div v-if="targetCharacterErrors.length" class="el-input-group__append">
            <help-tooltip type="error">
              <div v-for="error in targetCharacterErrors" :key="error.message">{{ error.message }}</div>
            </help-tooltip>
          </div>
        </div>
      </div>
      <div>
        <div class="el-input-group el-input-group--append el-input-group--prepend">
          <div class="el-input-group__prepend">How much</div>
          <el-select
            v-close-on-zoom
            :value="state.attentionType"
            placeholder="Attention level"
            :disabled="readonly"
            class="attention-type-select"
            @input="selectAttentionType"
          >
            <el-option
              v-for="type in attentionTypes"
              :key="type.value"
              :label="type.label"
              :value="type.value"
            />
          </el-select>
          <div v-if="attentionTypeErrors.length" class="el-input-group__append">
            <help-tooltip type="error">
              <div v-for="error in attentionTypeErrors" :key="error.message">{{ error.message }}</div>
            </help-tooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { getSourceCharacters, getTargetCharacters } from '~lib/common/utils/characters';
import { isNpc } from '~lib/common/utils/npc';
import { attentionTypes } from '~lib/common/utils/attentionTypes';
import nodeMixins from './mixins/NodeMixins';
import CharacterSelect from './CharacterSelect.vue';
import HelpTooltip from '~lib/common/components/HelpTooltip.vue';

export default {
  name: 'attention-check',
  components: {
    CharacterSelect,
    HelpTooltip,
  },
  mixins: [nodeMixins],
  props: ['readonly', 'emitter', 'ikey', 'getData', 'putData', 'control', 'checkType'],
  data() {
    return {
      attentionTypes,
      state: {
        sourceCharacterKey: null,
        targetCharacterKey: null,
        attentionType: null,
      },
    };
  },
  computed: {
    targetCharacters() {
      return getTargetCharacters(this.emitter.caseConf).
        filter(
          character =>
            character.key !== this.state.sourceCharacterKey &&
            character.key !== 'anyone'
        )
        .filter(character => {
          // Don't allow target to be NPC if source is NPC
          if (isNpc(this.state.sourceCharacterKey)) {
            return !isNpc(character.key)
          }
          return true;
        });
    },
    sourceCharacterErrors() {
      return this.getErrorsById('missingSourceCharacter');
    },
    targetCharacterErrors() {
      return this.getErrorsById('missingTargetCharacter');
    },
    attentionTypeErrors() {
      return this.getErrorsById('attentionType');
    },
  },
  created() {
    const data = this.getData(this.ikey);
    if (data) {
      this.state.sourceCharacterKey = data.sourceCharacterKey || null;
      this.state.targetCharacterKey = data.targetCharacterKey || null;
      this.state.attentionType = data.attentionType || null;
    }
    this.emitter.trigger('process', this.control);
  },
  mounted() {
    this.syncState();
    this.emitter.on('process', (nodeOrControl) => {
      // Update internal state when the node data changes
      // outside (e.g. from a CallAction)
      if (nodeOrControl === this.control) {
        this.syncState();
      }
    });
    this.update();
  },
  methods: {
    selectCharacter(key) {
      this.state.sourceCharacterKey = key;
      if (this.state.targetCharacterKey) {
        this.state.targetCharacterKey = null;
      }
      this.update();
    },
    selectTarget(key) {
      this.state.targetCharacterKey = key;
      this.update();
    },
    selectAttentionType(value) {
      this.state.attentionType = value;
      this.update();
    },
    syncState() {
      const data = this.getData(this.ikey);
      this.state.sourceCharacterKey = get(data, 'sourceCharacterKey');
      this.state.targetCharacterKey = get(data, 'targetCharacterKey');
      this.state.attentionType = get(data, 'attentionType');
    },
    update() {
      const typePrefix = isNpc(this.state.sourceCharacterKey) ? 'NPC_' : '';
      const type = typePrefix + this.checkType;
      this.putData(this.ikey, {
        type,
        sourceCharacterKey: this.state.sourceCharacterKey,
        targetCharacterKey: this.state.targetCharacterKey,
        attentionType: this.state.attentionType,
      });
      this.emitter.trigger('process', this.control);
    },
    getSourceCharacters,
  },
};
</script>

<style scoped>
.character-select,
.attention-type-select {
  display: table-cell;
  width: 100%;
}
</style>
